'use client'

import type { ImageLoaderProps } from 'next/image'
import Image from 'next/image'
import type { ImageBlokProps } from '../../schema/bloks/image-blok'
import { storyblokEditable } from '../storyblok-editable'

type ImageAssetWithFocus = NonNullable<ImageBlokProps['image']> & { focus?: string | null }

type Props = {
  blok: ImageBlokProps
  className?: string
  sizes?: string
  maxWidth?: number
}

export function ImageBlok({ blok, className, sizes, maxWidth }: Props) {
  const url = blok.image?.filename
  if (!url || !isStoryblokImage(url)) return null

  const focus = (blok.image as ImageAssetWithFocus | undefined)?.focus

  const size = getSize(url)
  const imageSizes =
    sizes ??
    (maxWidth != null
      ? `(max-width: ${maxWidth}px) 100vw, ${maxWidth}px`
      : '(max-width: 768px) 90vw, (max-width: 1200px) 60vw, 40vw')

  const loader = ({ src, width, quality }: ImageLoaderProps) =>
    storyblokImageLoader({ src, width: maxWidth ? Math.min(width, maxWidth) : width, quality })

  const pos = focus ? getObjectPositionFromStoryblokAsset(url, focus) : undefined

  return (
    <Image
      {...storyblokEditable(blok)}
      src={url}
      alt={blok.image.alt || ''}
      loader={loader}
      className={className}
      sizes={imageSizes}
      style={pos ? { objectPosition: pos } : undefined}
      width={size.width}
      height={size.height}
    />
  )
}

function storyblokImageLoader({ src, width, quality }: ImageLoaderProps) {
  return `${src}/m/${width}x0/filters:quality(${quality || 75})`
}

export function getSize(url: string) {
  const result = /(\d+)x(\d+)/.exec(url)
  if (!result) return { width: 500, height: 500 }

  return { width: Number(result[1]), height: Number(result[2]) }
}

function isStoryblokImage(url: string) {
  return url.startsWith('https://a.storyblok.com')
}

function getObjectPositionFromStoryblokAsset(imageUrl: string, focus: string): string {
  const match = focus.match(/(\d+)x(\d+)/)
  if (!match) return '50% 50%'

  const [x, y] = [Number(match[1]), Number(match[2])]
  const size = getSize(imageUrl)
  if (!size) return '50% 50%'

  const xPct = Math.max(0, Math.min(1, x / size.width)) * 100
  const yPct = Math.max(0, Math.min(1, y / size.height)) * 100

  return `${xPct.toFixed(1)}% ${yPct.toFixed(1)}%`
}
